import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, RefreshCw, X, AlertCircle, Check, Loader2, Mail } from "lucide-react";

/**
 * RecapHistory — small "Recaps sent" list for a player (iter53).
 *
 * Coach-facing widget that renders on the PlayerDetail page. Shows every
 * recap ever fired to this player, newest first, with:
 *   · Subject + sent-at timestamp
 *   · Email delivery status (✓ delivered / ⚠ failed with reason)
 *   · A one-tap "Resend" button that re-fires the same body via /recaps/{id}/resend
 *   · Click a row to view the full body in a modal
 *
 * Auto-refreshes when a fresh recap is dispatched anywhere in the app via
 * the `recaps:changed` custom event.
 */
export default function RecapHistory({ studentId, studentName = "" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(null); // recap object
  const [resendingId, setResendingId] = useState("");

  const load = async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/coach/players/${studentId}/recaps?limit=25`);
      setItems(data.items || []);
    } catch (e) {
      // Best-effort — silently show empty state on error to avoid noise.
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load();   }, [studentId]);

  // Refresh when a recap is sent from anywhere in the app.
  useEffect(() => {
    const handler = (e) => {
      if (!e?.detail?.studentId || e.detail.studentId === studentId) load();
    };
    window.addEventListener("recaps:changed", handler);
    return () => window.removeEventListener("recaps:changed", handler);
     
  }, [studentId]);

  const resend = async (recap) => {
    if (resendingId) return;
    setResendingId(recap.id);
    try {
      const { data } = await api.post(`/coach/players/${studentId}/recaps/${recap.id}/resend`);
      if (data.email_sent) {
        toast.success("Recap resent — email + in-app");
      } else if (data.notification_sent) {
        toast.warning(`Only in-app notification sent (email failed): ${data.email_error || "unknown"}`);
      } else {
        toast.error("Resend failed — please try again.");
      }
      window.dispatchEvent(new CustomEvent("recaps:changed", { detail: { studentId } }));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Resend failed.");
    } finally {
      setResendingId("");
    }
  };

  // Nothing sent yet — hide the widget entirely so it doesn't clutter the
  // page for brand-new players. It re-appears on the first send.
  if (!loading && items.length === 0) return null;

  const fmt = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    } catch { return iso; }
  };

  return (
    <div className="card-surface p-4 fade-up d3" data-testid="recap-history">
      <div className="flex items-center justify-between mb-3">
        <div className="label-mono flex items-center gap-1.5">
          <Mail className="w-3 h-3 text-[#CCFF00]" />
          Recaps sent {items.length > 0 && <span className="text-[#525252] font-normal">· {items.length}</span>}
        </div>
        <button
          onClick={load}
          className="text-[10px] uppercase tracking-wider text-[#525252] hover:text-[#CCFF00] inline-flex items-center gap-1"
          data-testid="recap-history-refresh"
          title="Refresh"
        >
          <RefreshCw className="w-3 h-3" />
        </button>
      </div>

      {loading ? (
        <div className="text-[10px] text-[#525252]">Loading…</div>
      ) : (
        <div className="space-y-2">
          {items.map((r) => {
            const failed = !r.email_sent && r.email_error;
            return (
              <div
                key={r.id}
                className={`border transition-colors ${failed ? "border-[#FF4500]/40" : "border-[#262626]"} hover:border-[#525252]`}
                data-testid={`recap-history-row-${r.id}`}
              >
                <div className="flex items-center gap-2 px-3 py-2">
                  <button
                    onClick={() => setPreviewing(r)}
                    className="flex-1 text-left group"
                    data-testid={`recap-history-open-${r.id}`}
                  >
                    <div className="text-xs text-[#E5E5E5] leading-tight line-clamp-1 group-hover:text-[#CCFF00] transition-colors">
                      {r.subject || "Recap"}
                    </div>
                    <div className="text-[10px] text-[#525252] mt-0.5 flex items-center gap-2">
                      <span>{fmt(r.created_at)}</span>
                      <span className="text-[#404040]">·</span>
                      {r.email_sent ? (
                        <span className="inline-flex items-center gap-1 text-[#A3A3A3]">
                          <Check className="w-2.5 h-2.5" />
                          Delivered
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#FF4500]" title={r.email_error || "Email failed"}>
                          <AlertCircle className="w-2.5 h-2.5" />
                          Email failed
                        </span>
                      )}
                      {r.tone && (
                        <>
                          <span className="text-[#404040]">·</span>
                          <span className="uppercase tracking-wider">{r.tone}</span>
                        </>
                      )}
                    </div>
                  </button>
                  <button
                    onClick={() => resend(r)}
                    disabled={resendingId === r.id}
                    className="text-[10px] uppercase tracking-wider px-2 py-1 border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] transition-colors inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid={`recap-history-resend-${r.id}`}
                    title="Send this recap again — same subject + body"
                  >
                    {resendingId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    {resendingId === r.id ? "…" : "Resend"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full-body preview modal (portaled to escape .fade-up ancestor transforms) */}
      {previewing && createPortal((
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setPreviewing(null)}
          data-testid="recap-history-preview-modal"
        >
          <div
            className="bg-[#0A0A0A] border border-[#262626] w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626]">
              <div>
                <div className="text-sm font-medium leading-tight">{previewing.subject}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mt-0.5">
                  {fmt(previewing.created_at)} · from {previewing.coach_name || "Coach"}
                  {studentName && ` · to ${studentName}`}
                </div>
              </div>
              <button onClick={() => setPreviewing(null)} className="p-1 hover:bg-[#141414]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-5">
              <pre className="text-sm text-[#E5E5E5] whitespace-pre-wrap leading-relaxed font-sans" data-testid="recap-history-preview-body">
                {previewing.body}
              </pre>
              {previewing.email_error && (
                <div className="mt-4 text-[10px] text-[#FF4500] border-t border-[#262626] pt-3" data-testid="recap-history-preview-error">
                  <strong>Email delivery failed:</strong> {previewing.email_error}
                </div>
              )}
            </div>
            <div className="border-t border-[#262626] px-5 py-3 flex items-center justify-end gap-2">
              <button
                onClick={() => setPreviewing(null)}
                className="text-xs px-4 py-2 border border-[#262626] hover:border-[#525252]"
              >
                Close
              </button>
              <button
                onClick={() => { resend(previewing); setPreviewing(null); }}
                disabled={resendingId === previewing.id}
                className="text-xs px-4 py-2 bg-[#CCFF00] text-[#0A0A0A] font-semibold inline-flex items-center gap-2 disabled:opacity-50"
              >
                {resendingId === previewing.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Resend now
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
